/**
 * Adaptador de almacenamiento local de alta capacidad (IndexedDB) para K-LyDapp.
 * Resuelve de forma definitiva el límite de 5 MB de localStorage en iOS / WebKit,
 * permitiendo almacenar decenas o cientos de megabytes en modo fuera de línea sin saturar la cuota.
 */

const DB_NAME = 'klydapp_offline_store';
const STORE_NAME = 'app_state';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function getDb(): Promise<IDBDatabase> {
    if (typeof window === 'undefined' || !window.indexedDB) {
        return Promise.reject(new Error('IndexedDB no está disponible en este entorno.'));
    }

    if (dbPromise) return dbPromise;

    dbPromise = new Promise((resolve, reject) => {
        try {
            const req = window.indexedDB.open(DB_NAME, DB_VERSION);

            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME);
                }
            };

            req.onsuccess = () => {
                const db = req.result;
                db.onversionchange = () => {
                    db.close();
                    dbPromise = null;
                };
                resolve(db);
            };

            req.onerror = () => {
                dbPromise = null;
                reject(req.error || new Error('Error al inicializar IndexedDB'));
            };

            req.onblocked = () => {
                console.warn('[IndexedDB] Apertura bloqueada por otra pestaña.');
            };
        } catch (err) {
            dbPromise = null;
            reject(err);
        }
    });

    return dbPromise;
}

/**
 * Guarda un objeto o valor en IndexedDB.
 */
export async function setIndexedDb(key: string, value: any): Promise<void> {
    try {
        const db = await getDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const req = store.put(value, key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    } catch (err) {
        console.warn(`[IndexedDB] Error al guardar clave '${key}':`, err);
    }
}

/**
 * Recupera un valor almacenado en IndexedDB por su clave.
 */
export async function getIndexedDb<T>(key: string): Promise<T | null> {
    try {
        const db = await getDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const req = store.get(key);
            req.onsuccess = () => resolve((req.result !== undefined ? req.result : null) as T | null);
            req.onerror = () => reject(req.error);
        });
    } catch (err) {
        console.warn(`[IndexedDB] Error al leer clave '${key}':`, err);
        return null;
    }
}

/**
 * Elimina una clave de IndexedDB.
 */
export async function removeIndexedDb(key: string): Promise<void> {
    try {
        const db = await getDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const req = store.delete(key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    } catch (err) {
        console.warn(`[IndexedDB] Error al eliminar clave '${key}':`, err);
    }
}

/**
 * Limpia todas las entradas de IndexedDB.
 */
export async function clearIndexedDb(): Promise<void> {
    try {
        const db = await getDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const req = store.clear();
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    } catch (err) {
        console.warn('[IndexedDB] Error al limpiar la base de datos:', err);
    }
}
